import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { extractErrorDetail } from "./validators";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog, DialogContent, Box, Button, Typography, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Chip,
  CircularProgress, Alert, Switch,
  Collapse, Divider,
} from "@mui/material";
import {
  Add, Delete, Edit, Save, Close, ExpandMore, ExpandLess,
  Science, Biotech, Settings, ChevronRight,
} from "@mui/icons-material";
import ConfirmDialog from "./ConfirmDialog";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// 2026-10-05 fix — real bug, found and reported directly ("failed to load
// labs"): every axios call in this file sent NO Authorization header at
// all, so /api/config/labs always 401'd regardless of who clicked it. This
// panel now lives only inside AdminDashboard.js (Configure Lab moved out
// of the regular staff NavUserMenu, since lab/report-type config is an
// admin capability) — same admin_token convention AdminDashboard.js's own
// authHeaders() already uses.
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

/* ── Design tokens — Labarium brand ── */
const T = {
  navy:    "#001f4d",
  navyMid: "#00357a",
  navyLight:"#004299",
  border:  "rgba(201,169,110,0.22)",
  sidebarBg: "rgba(0,31,77,0.98)",
  gold:    "#c9a96e",
  goldLight:"#dfc08a",
  text:    "#ffffff",
  textSoft:"rgba(255,255,255,0.75)",
  muted:   "rgba(255,255,255,0.4)",
  surface: "#f4f7ff",
  white:   "#ffffff",
  red:     "#ef4444",
  font:    "'Inter', sans-serif",
  mono:    "'JetBrains Mono', monospace",
  display: "'Playfair Display', serif",
};

const LOGO_OPTIONS = [
  { value: "labarium",     label: "Labarium" },
  { value: "medlake",      label: "MedLake" },
  { value: "infinitilabs", label: "InfinitiLabs" },
  // 2026-10-29 addition — ApexLabs' own lab_configs entry (default_configs.py)
  // now exists with logo_key "apexlabs_logo"; this dropdown had no option for
  // it at all, so admin editing that record would see a blank Logo field.
  { value: "apexlabs_logo", label: "ApexLabs" },
];

const EXTRACTION_TYPES = [
  { value: "blood_panel", label: "Blood Panel (standard lab results)" },
  { value: "drug_screen", label: "Drug Screen (EIA + LC-MS/MS)" },
  { value: "tox_panel",   label: "Toxicology Panel (InfinitiLabs style)" },
  { value: "pcr_panel",   label: "PCR / Pathogen Panel" },
];
const SUMMARY_TYPES_PATIENT = [
  { value: "patient_standard", label: "Standard Blood Panel" },
  { value: "patient_tox",      label: "Toxicology" },
  { value: "patient_pcr",      label: "PCR / Pathogen" },
];
const SUMMARY_TYPES_CLINICIAN = [
  { value: "clinician_standard", label: "Standard (Evidence-based)" },
  { value: "clinician_tox",      label: "Toxicology (Clinical)" },
  { value: "clinician_pcr",      label: "PCR / Pathogen (Clinical)" },
];
const REPORT_TEMPLATES = [
  { value: "standard",    label: "Standard (blood/PCR)" },
  { value: "drug_screen", label: "Drug Screen" },
  { value: "tox_panel",   label: "Toxicology Panel" },
  { value: "pcr",         label: "PCR" },
];

/* ── Shared MUI sx overrides ── */
const darkField = {
  "& .MuiInputBase-root": {
    background: "rgba(255,255,255,0.04)",
    borderRadius: "10px",
    color: T.text,
    fontFamily: T.font,
    fontSize: "0.85rem",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: T.border, borderRadius: "10px" },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.gold },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: T.gold, borderWidth: "1px",
  },
  "& .MuiInputLabel-root": { color: T.muted, fontFamily: T.font, fontSize: "0.82rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.gold },
  "& .MuiFormHelperText-root": { color: T.muted, fontFamily: T.font, fontSize: "0.72rem" },
  "& .MuiSelect-icon": { color: T.muted },
  "& .MuiSwitch-switchBase.Mui-checked": { color: T.gold },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "rgba(192,168,140,0.5)" },
};

const lightField = {
  "& .MuiInputBase-root": {
    background: "#fff",
    borderRadius: "10px",
    fontSize: "0.85rem",
    fontFamily: T.font,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1DCE8", borderRadius: "10px" },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.navyMid },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: T.navyMid, borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.navyMid },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem" },
};

/* ══════════════════════════════════════════════════════
   FieldEditor
══════════════════════════════════════════════════════ */
function FieldEditor({ fields, onChange, dark }) {
  const addField = () =>
    onChange([...fields, { field: "", aliases: [], required: true }]);
  const updateField = (i, key, val) =>
    onChange(fields.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));
  const removeField = (i) => onChange(fields.filter((_, idx) => idx !== i));
  const updateAliases = (i, raw) =>
    updateField(i, "aliases", raw.split(",").map((s) => s.trim()).filter(Boolean));

  const sx = dark ? darkField : lightField;
  const borderColor = dark ? T.border : "#D1DCE8";
  const textColor = dark ? T.text : "inherit";
  const mutedColor = dark ? T.muted : "#6B7280";

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.78rem", fontFamily: T.mono, color: mutedColor, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Metadata Fields ({fields.length})
        </Typography>
        <Button
          size="small" startIcon={<Add sx={{ fontSize: "0.9rem !important" }} />}
          onClick={addField}
          sx={{
            fontFamily: T.font, fontSize: "0.75rem", color: T.gold,
            border: `1px solid ${T.border}`, borderRadius: "6px", px: 1.5, py: 0.5,
            "&:hover": { background: "rgba(192,168,140,0.12)", borderColor: T.gold },
          }}
        >
          Add Field
        </Button>
      </Box>

      {fields.length === 0 ? (
        <Box sx={{
          border: `1px dashed ${dark ? T.border : "#D1DCE8"}`,
          borderRadius: "8px", p: 3,
          textAlign: "center", color: mutedColor,
          fontSize: "0.8rem", fontFamily: T.font,
        }}>
          No metadata fields defined. Click Add Field to configure extraction targets.
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: "8px", overflow: "hidden" }}>
          <Box sx={{
            display: "grid", gridTemplateColumns: "1fr 2fr 80px 40px",
            px: 2, py: 1,
            background: dark ? "rgba(255,255,255,0.03)" : "#F1F5F9",
            borderBottom: `1px solid ${borderColor}`,
          }}>
            {["Field Name", "Aliases (comma-separated)", "Required", ""].map((h) => (
              <Typography key={h} sx={{ fontSize: "0.7rem", fontFamily: T.mono, color: mutedColor, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {h}
              </Typography>
            ))}
          </Box>
          {fields.map((f, i) => (
            <Box key={i} sx={{
              display: "grid", gridTemplateColumns: "1fr 2fr 80px 40px",
              alignItems: "center", px: 2, py: 1,
              borderBottom: i < fields.length - 1 ? `1px solid ${borderColor}` : "none",
              "&:hover": { background: dark ? "rgba(255,255,255,0.02)" : "#F8FAFC" },
            }}>
              <TextField size="small" variant="standard" value={f.field}
                onChange={(e) => updateField(i, "field", e.target.value)}
                sx={{ ...sx, "& .MuiInput-underline:before": { borderColor: "transparent" } }}
                inputProps={{ style: { color: textColor, fontFamily: T.font, fontSize: "0.82rem" } }}
              />
              <TextField size="small" variant="standard"
                value={(f.aliases || []).join(", ")}
                onChange={(e) => updateAliases(i, e.target.value)}
                placeholder="alias1, alias2"
                sx={{ ...sx, "& .MuiInput-underline:before": { borderColor: "transparent" } }}
                inputProps={{ style: { color: textColor, fontFamily: T.font, fontSize: "0.82rem" } }}
              />
              <Switch size="small" checked={!!f.required}
                onChange={(e) => updateField(i, "required", e.target.checked)}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: T.gold }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: T.goldLight } }}
              />
              <IconButton size="small" onClick={() => removeField(i)}
                sx={{ color: T.muted, "&:hover": { color: T.red, background: "rgba(255,91,106,0.1)" } }}>
                <Delete sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   ReportTypeEditor
══════════════════════════════════════════════════════ */
function ReportTypeEditor({ rt, onSave, onCancel, onDelete, dark }) {
  const [form, setForm] = useState({
    id: "", name: "", description: "",
    extraction_type: "blood_panel",
    summary_type_patient: "patient_standard",
    summary_type_clinician: "clinician_standard",
    report_template: "standard",
    age_field: "DOB", sex_field: "Gender", acc_field: "Sample ID",
    metadata_prompt_override: "",
    test_prompt_override: "",
    metadata_fields: [],
    ...rt,
  });
  const [showPrompts, setShowPrompts] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sx = dark ? darkField : lightField;
  const bg = dark ? T.navyLight : "#fff";
  const border = dark ? T.border : "#E2EAF4";
  const text = dark ? T.text : T.navy;
  const muted = dark ? T.muted : "#6B7280";

  const SelectField = ({ label, value, onChange, options, width }) => (
    <FormControl size="small" sx={{ minWidth: width || 200, ...sx }}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onChange(e.target.value)}
        sx={{ color: text, fontFamily: T.font, fontSize: "0.83rem",
          "& .MuiSelect-icon": { color: muted },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
        }}
        MenuProps={{ PaperProps: { sx: {
          background: dark ? T.navyMid : "#fff",
          border: `1px solid ${border}`,
          "& .MuiMenuItem-root": { fontFamily: T.font, fontSize: "0.83rem", color: text },
          "& .MuiMenuItem-root:hover": { background: dark ? "rgba(192,168,140,0.15)" : "#EFF3FB" },
          "& .MuiMenuItem-root.Mui-selected": { background: dark ? "rgba(192,168,140,0.2)" : "#DDE6F7" },
        }}}}>
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      <Box sx={{
        background: bg, border: `1px solid ${border}`,
        borderRadius: "10px", p: 2.5, mb: 2,
        boxShadow: dark ? "0 4px 24px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
      }}>
        {/* Identity row */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "160px 1fr 1.6fr" }, gap: 2, mb: 2 }}>
          <TextField label="Report Type ID" size="small" sx={sx}
            value={form.id}
            onChange={(e) => set("id", e.target.value.toLowerCase().replace(/\s/g, "_"))}
            disabled={!!rt?.id}
            helperText="Unique ID e.g. blood_panel"
            inputProps={{ style: { fontFamily: T.mono, fontSize: "0.82rem", color: text } }}
          />
          <TextField label="Display Name" size="small" sx={sx}
            value={form.name} onChange={(e) => set("name", e.target.value)}
            inputProps={{ style: { color: text, fontFamily: T.font } }}
          />
          <TextField label="Description" size="small" sx={sx}
            value={form.description} onChange={(e) => set("description", e.target.value)}
            inputProps={{ style: { color: text, fontFamily: T.font } }}
          />
        </Box>

        {/* Selects */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
          <SelectField label="Extraction Type" value={form.extraction_type}
            onChange={(v) => set("extraction_type", v)} options={EXTRACTION_TYPES} />
          <SelectField label="Report Template" value={form.report_template}
            onChange={(v) => set("report_template", v)} options={REPORT_TEMPLATES} />
          <SelectField label="Patient Summary" value={form.summary_type_patient}
            onChange={(v) => set("summary_type_patient", v)} options={SUMMARY_TYPES_PATIENT} />
          <SelectField label="Clinician Summary" value={form.summary_type_clinician}
            onChange={(v) => set("summary_type_clinician", v)} options={SUMMARY_TYPES_CLINICIAN} />
        </Box>

        {/* Demographics */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, mb: 2 }}>
          <TextField label="Age Field Key" size="small" sx={sx}
            value={form.age_field} onChange={(e) => set("age_field", e.target.value)}
            helperText="Metadata key for DOB/age"
            inputProps={{ style: { color: text, fontFamily: T.mono, fontSize: "0.82rem" } }}
          />
          <TextField label="Sex Field Key" size="small" sx={sx}
            value={form.sex_field} onChange={(e) => set("sex_field", e.target.value)}
            inputProps={{ style: { color: text, fontFamily: T.mono, fontSize: "0.82rem" } }}
          />
          <TextField label="Accession Field Key" size="small" sx={sx}
            value={form.acc_field} onChange={(e) => set("acc_field", e.target.value)}
            inputProps={{ style: { color: text, fontFamily: T.mono, fontSize: "0.82rem" } }}
          />
        </Box>

        <Divider sx={{ my: 2, borderColor: border }} />
        <FieldEditor fields={form.metadata_fields}
          onChange={(fields) => set("metadata_fields", fields)} dark={dark} />

        <Divider sx={{ my: 2, borderColor: border }} />

        {/* Advanced prompts */}
        <Button size="small"
          startIcon={showPrompts ? <ExpandLess sx={{ fontSize: "0.9rem !important" }} /> : <ExpandMore sx={{ fontSize: "0.9rem !important" }} />}
          onClick={() => setShowPrompts(!showPrompts)}
          sx={{ fontFamily: T.font, fontSize: "0.75rem", color: muted, textTransform: "none",
            "&:hover": { color: T.gold, background: "transparent" } }}>
          {showPrompts ? "Hide" : "Show"} Prompt Overrides (Advanced)
        </Button>
        <Collapse in={showPrompts}>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField label="Metadata Prompt Override" multiline rows={4} fullWidth size="small" sx={sx}
              value={form.metadata_prompt_override || ""}
              onChange={(e) => set("metadata_prompt_override", e.target.value || null)}
              placeholder="Leave blank to use the built-in metadata prompt for this extraction type."
              inputProps={{ style: { color: text, fontFamily: T.mono, fontSize: "0.78rem" } }}
            />
            <TextField label="Test Extraction Prompt Override" multiline rows={4} fullWidth size="small" sx={sx}
              value={form.test_prompt_override || ""}
              onChange={(e) => set("test_prompt_override", e.target.value || null)}
              placeholder="Leave blank to use the built-in Gemini extraction prompt for this extraction type."
              inputProps={{ style: { color: text, fontFamily: T.mono, fontSize: "0.78rem" } }}
            />
          </Box>
        </Collapse>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1, mt: 2.5, justifyContent: "flex-end" }}>
          {onDelete && (
            <Button size="small" variant="outlined" startIcon={<Delete sx={{ fontSize: "0.9rem !important" }} />}
              onClick={onDelete}
              sx={{ fontFamily: T.font, fontSize: "0.78rem", color: T.red, borderColor: T.red,
                "&:hover": { background: "rgba(255,91,106,0.08)", borderColor: T.red } }}>
              Delete
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={onCancel}
            sx={{ fontFamily: T.font, fontSize: "0.78rem", color: muted, borderColor: border,
              "&:hover": { borderColor: muted } }}>
            Cancel
          </Button>
          <Button size="small" variant="contained"
            startIcon={<Save sx={{ fontSize: "0.9rem !important" }} />}
            onClick={() => onSave(form)} disabled={!form.id || !form.name}
            sx={{ fontFamily: T.font, fontSize: "0.78rem",
              background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
              color: "#fff", boxShadow: "none",
              "&:hover": { background: T.goldLight, boxShadow: "0 4px 12px rgba(192,168,140,0.4)" },
              "&:disabled": { background: "rgba(255,255,255,0.1)", color: muted },
            }}>
            Save Report Type
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════
   LabEditor
══════════════════════════════════════════════════════ */
function LabEditor({ lab, onSave, onCancel, isNew }) {
  const [form, setForm] = useState({
    lab_id: "", display_name: "", short_name: "",
    address: "", clia: "", npi: "", medical_director: "", logo_key: "labarium",
    report_types: {},
    ...lab,
  });
  const [editingRT, setEditingRT] = useState(null);
  const [addingRT,  setAddingRT]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [confirmDeleteRT, setConfirmDeleteRT] = useState(null); // 2026-10-05 — real modal, not window.confirm
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveRT = async (rtData) => {
    setSaving(true); setError("");
    try {
      if (isNew) {
        setForm((f) => ({ ...f, report_types: { ...f.report_types, [rtData.id]: rtData } }));
      } else {
        await axios.put(`${API}/api/config/labs/${form.lab_id}/report_types/${rtData.id}`, rtData, { headers: authHeaders() });
        setForm((f) => ({ ...f, report_types: { ...f.report_types, [rtData.id]: rtData } }));
      }
      setEditingRT(null); setAddingRT(false);
    } catch { setError("Failed to save report type."); }
    setSaving(false);
  };

  const deleteRT = async (rtId) => {
    try {
      if (!isNew) await axios.delete(`${API}/api/config/labs/${form.lab_id}/report_types/${rtId}`, { headers: authHeaders() });
      setForm((f) => { const rts = { ...f.report_types }; delete rts[rtId]; return { ...f, report_types: rts }; });
      setEditingRT(null);
    } catch { setError("Failed to delete report type."); }
    setConfirmDeleteRT(null);
  };

  const handleSaveLab = async () => {
    setSaving(true); setError("");
    try { await onSave(form); }
    catch (e) { setError(extractErrorDetail(e, "Failed to save lab.")); }
    setSaving(false);
  };

  const rtList = Object.values(form.report_types || {});

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: T.font, fontSize: "0.83rem" }}>{error}</Alert>
      )}

      {/* Lab identity */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: T.mono, fontSize: "0.7rem", color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>
          Lab Identity
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "140px 1fr 120px" }, gap: 2, mb: 2 }}>
          <TextField label="Lab ID" size="small" sx={lightField}
            value={form.lab_id} disabled={!isNew}
            onChange={(e) => set("lab_id", e.target.value.toLowerCase().replace(/\s/g, "_"))}
            helperText="Unique key e.g. medlake"
            inputProps={{ style: { fontFamily: T.mono, fontSize: "0.82rem" } }}
          />
          <TextField label="Display Name" size="small" sx={lightField}
            value={form.display_name} onChange={(e) => set("display_name", e.target.value)} />
          <TextField label="Short Name" size="small" sx={lightField}
            value={form.short_name} onChange={(e) => set("short_name", e.target.value)} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr 140px" }, gap: 2, mb: 2 }}>
          <TextField label="Address" size="small" sx={lightField}
            value={form.address} onChange={(e) => set("address", e.target.value)} />
          <TextField label="CLIA #" size="small" sx={lightField}
            value={form.clia} onChange={(e) => set("clia", e.target.value)} />
          <TextField label="NPI" size="small" sx={lightField}
            value={form.npi || ""} onChange={(e) => set("npi", e.target.value)}
            helperText="Provider / practice NPI"
            inputProps={{ style: { fontFamily: T.mono, fontSize: "0.82rem" } }} />
          <TextField label="Medical Director" size="small" sx={lightField}
            value={form.medical_director} onChange={(e) => set("medical_director", e.target.value)} />
          <FormControl size="small" sx={lightField}>
            <InputLabel sx={{ fontSize: "0.82rem" }}>Logo</InputLabel>
            <Select value={form.logo_key} label="Logo" onChange={(e) => set("logo_key", e.target.value)}
              sx={{ fontSize: "0.83rem", fontFamily: T.font }}>
              {LOGO_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontFamily: T.font, fontSize: "0.83rem" }}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Report types */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontFamily: T.mono, fontSize: "0.7rem", color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Report Types ({rtList.length})
          </Typography>
          <Button size="small" startIcon={<Add sx={{ fontSize: "0.9rem !important" }} />}
            onClick={() => { setAddingRT(true); setEditingRT(null); }}
            sx={{ fontFamily: T.font, fontSize: "0.75rem", color: T.goldLight,
              border: "1px solid #D1DCE8", borderRadius: "6px", px: 1.5, py: 0.5,
              "&:hover": { borderColor: T.gold, background: "rgba(192,168,140,0.07)" } }}>
            Add Report Type
          </Button>
        </Box>

        <AnimatePresence>
          {addingRT && (
            <ReportTypeEditor
              rt={{ id: "", name: "", metadata_fields: [] }}
              onSave={saveRT}
              onCancel={() => setAddingRT(false)}
              dark={false}
            />
          )}
        </AnimatePresence>

        {rtList.length === 0 && !addingRT && (
          <Box sx={{
            border: "1px dashed #D1DCE8", borderRadius: "10px",
            p: 4, textAlign: "center", color: "#94A3B8",
            fontFamily: T.font, fontSize: "0.83rem",
          }}>
            <Science sx={{ fontSize: 32, opacity: 0.3, mb: 1, display: "block", mx: "auto" }} />
            No report types yet. Add one to configure extraction.
          </Box>
        )}

        <AnimatePresence>
          {rtList.map((rt) =>
            editingRT === rt.id ? (
              <ReportTypeEditor key={rt.id} rt={rt}
                onSave={saveRT}
                onCancel={() => setEditingRT(null)}
                onDelete={() => setConfirmDeleteRT(rt.id)}
                dark={false}
              />
            ) : (
              <motion.div key={rt.id} layout
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  p: 1.5, mb: 1, border: "1px solid #E2EAF4", borderRadius: "8px",
                  background: "#fff",
                  "&:hover": { borderColor: T.gold, boxShadow: "0 2px 8px rgba(192,168,140,0.12)" },
                  transition: "all 0.15s",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: T.gold, flexShrink: 0,
                    }} />
                    <Box>
                      <Typography sx={{ fontFamily: T.font, fontSize: "0.85rem", fontWeight: 500, color: T.navy }}>
                        {rt.name}
                      </Typography>
                      <Typography sx={{ fontFamily: T.mono, fontSize: "0.72rem", color: "#94A3B8" }}>
                        {rt.id} · {rt.extraction_type}
                        {rt.description ? ` · ${rt.description}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton size="small" onClick={() => { setEditingRT(rt.id); setAddingRT(false); }}
                      sx={{ color: "#94A3B8", "&:hover": { color: T.goldLight, background: "rgba(192,168,140,0.12)" } }}>
                      <Edit sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirmDeleteRT(rt.id)}
                      sx={{ color: "#94A3B8", "&:hover": { color: T.red, background: "rgba(255,91,106,0.08)" } }}>
                      <Delete sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  </Box>
                </Box>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3, pt: 2, borderTop: "1px solid #E2EAF4" }}>
        <Button onClick={onCancel}
          sx={{ fontFamily: T.font, fontSize: "0.83rem", color: "#64748B",
            "&:hover": { background: "#F1F5F9" } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Save sx={{ fontSize: "1rem !important" }} />}
          onClick={handleSaveLab} disabled={saving || !form.lab_id || !form.display_name}
          sx={{
            fontFamily: T.font, fontSize: "0.83rem",
            background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`,
            boxShadow: "none",
            "&:hover": { background: T.goldLight, boxShadow: "0 4px 16px rgba(0,53,122,0.25)" },
            "&:disabled": { background: "#E2EAF4", color: "#94A3B8" },
          }}>
          {isNew ? "Create Lab" : "Save Changes"}
        </Button>
      </Box>

      <ConfirmDialog
        open={!!confirmDeleteRT}
        title="Delete report type?"
        message={confirmDeleteRT ? `Delete report type "${confirmDeleteRT}"? This can't be undone.` : ""}
        onConfirm={() => deleteRT(confirmDeleteRT)}
        onCancel={() => setConfirmDeleteRT(null)}
      />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════
   Main LabConfigPanel
══════════════════════════════════════════════════════ */
export default function LabConfigPanel({ open, onClose }) {
  const [labs,            setLabs]            = useState([]);
  const [selectedLab,     setSelectedLab]     = useState(null);
  const [selectedLabData, setSelectedLabData] = useState(null);
  const [editingLab,      setEditingLab]      = useState(null);
  const [addingLab,       setAddingLab]       = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [detailLoading,   setDetailLoading]   = useState(false);
  const [error,           setError]           = useState("");
  const [confirmDeleteLab, setConfirmDeleteLab] = useState(null); // 2026-10-05 — real modal, not window.confirm

  const loadLabs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/config/labs`, { headers: authHeaders() });
      setLabs(res.data);
    } catch { setError("Failed to load labs."); }
    setLoading(false);
  }, []);

  const selectLab = useCallback(async (labId) => {
    setSelectedLab(labId);
    setEditingLab(null);
    setAddingLab(false);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/config/labs/${labId}`, { headers: authHeaders() });
      setSelectedLabData(res.data);
    } catch { setSelectedLabData(null); }
    setDetailLoading(false);
  }, []);

  useEffect(() => { if (open) loadLabs(); }, [open, loadLabs]);

  const saveLab = async (data) => {
    if (addingLab) {
      await axios.post(`${API}/api/config/labs`, data, { headers: authHeaders() });
    } else {
      await axios.put(`${API}/api/config/labs/${data.lab_id}`, data, { headers: authHeaders() });
    }
    await loadLabs();
    setEditingLab(null);
    setAddingLab(false);
    await selectLab(data.lab_id);
  };

  const deleteLab = async (labId) => {
    await axios.delete(`${API}/api/config/labs/${labId}`, { headers: authHeaders() });
    await loadLabs();
    if (selectedLab === labId) { setSelectedLab(null); setSelectedLabData(null); }
    setConfirmDeleteLab(null);
  };

  const rtValues = Object.values(selectedLabData?.report_types || {});

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: {
        height: "92vh", borderRadius: "20px", overflow: "hidden",
        background: T.surface,
        boxShadow: "0 40px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(201,169,110,0.12)",
      }}}>

      {/* ── Title bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 3.5, py: 2.5,
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
        borderBottom: `1px solid ${T.border}`,
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorative ring */}
        <Box sx={{ position: "absolute", top: -60, right: -60, width: 180, height: 180, borderRadius: "50%", border: "1px solid rgba(201,169,110,0.08)" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: "10px",
            background: "rgba(201,169,110,0.15)",
            border: "1px solid rgba(201,169,110,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Settings sx={{ color: T.gold, fontSize: "1.1rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: "1.05rem", color: T.text, letterSpacing: "-0.01em" }}>
              Lab Configuration
            </Typography>
            <Typography sx={{ fontFamily: T.mono, fontSize: "0.62rem", color: T.muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {labs.length} Lab{labs.length !== 1 ? "s" : ""} Configured
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}
          sx={{ color: T.muted, borderRadius: "10px", "&:hover": { color: T.text, background: "rgba(255,255,255,0.08)" } }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ display: "flex", p: 0, overflow: "hidden" }}>

        {/* ── Sidebar ── */}
        <Box sx={{
          width: 270, flexShrink: 0,
          background: T.sidebarBg,
          borderRight: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column",
        }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${T.border}` }}>
            <Button fullWidth size="small"
              startIcon={<Add sx={{ fontSize: "0.9rem !important" }} />}
              onClick={() => { setAddingLab(true); setEditingLab(null); setSelectedLab(null); }}
              sx={{
                fontFamily: T.font, fontSize: "0.82rem", fontWeight: 600, color: T.gold,
                border: `1px solid rgba(201,169,110,0.35)`, borderRadius: "10px", py: 1,
                background: "rgba(201,169,110,0.07)",
                letterSpacing: "0.02em",
                "&:hover": { background: "rgba(201,169,110,0.16)", borderColor: T.gold },
              }}>
              New Lab
            </Button>
          </Box>

          <Box sx={{ overflowY: "auto", flex: 1, py: 0.5 }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress size={20} sx={{ color: T.gold }} />
              </Box>
            ) : labs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: T.muted, fontFamily: T.font, fontSize: "0.8rem" }}>
                No labs yet
              </Box>
            ) : (
              <AnimatePresence>
                {labs.map((lab, i) => {
                  const rtCount = Object.keys(lab.report_types || {}).length;
                  const isSelected = selectedLab === lab.lab_id;
                  return (
                    <motion.div key={lab.lab_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}>
                      <Box
                        onClick={() => selectLab(lab.lab_id)}
                        sx={{
                          px: 2, py: 1.5, cursor: "pointer",
                          borderLeft: isSelected ? `3px solid ${T.gold}` : "3px solid transparent",
                          background: isSelected ? "rgba(192,168,140,0.12)" : "transparent",
                          "&:hover": { background: isSelected ? "rgba(192,168,140,0.15)" : "rgba(255,255,255,0.03)" },
                          transition: "all 0.15s",
                          position: "relative",
                        }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                              fontFamily: T.display, fontWeight: 600, fontSize: "0.85rem",
                              color: isSelected ? T.text : "#A8BDCF",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>
                              {lab.display_name}
                            </Typography>
                            <Typography sx={{ fontFamily: T.mono, fontSize: "0.65rem", color: T.muted }}>
                              {lab.lab_id}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
                            <Box sx={{
                              background: rtCount > 0 ? "rgba(192,168,140,0.2)" : "rgba(255,255,255,0.05)",
                              color: rtCount > 0 ? T.gold : T.muted,
                              fontFamily: T.mono, fontSize: "0.65rem",
                              px: 0.8, py: 0.2, borderRadius: "4px",
                              minWidth: 20, textAlign: "center",
                            }}>
                              {rtCount}
                            </Box>
                            {isSelected && <ChevronRight sx={{ color: T.gold, fontSize: "0.9rem" }} />}
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </Box>
        </Box>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 3, background: T.surface }}>
          {error && <Alert severity="error" sx={{ mb: 2, fontFamily: T.font }}>{error}</Alert>}

          <AnimatePresence mode="wait">
            {addingLab && (
              <motion.div key="adding"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: "1.1rem", color: T.navy }}>
                    New Lab
                  </Typography>
                  <Typography sx={{ fontFamily: T.font, fontSize: "0.8rem", color: "#64748B", mt: 0.3 }}>
                    Configure lab identity and report types
                  </Typography>
                </Box>
                <LabEditor lab={{}} isNew onSave={saveLab} onCancel={() => setAddingLab(false)} />
              </motion.div>
            )}

            {!addingLab && editingLab && (
              <motion.div key="editing"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: "1.1rem", color: T.navy }}>
                    {editingLab.display_name}
                  </Typography>
                  <Typography sx={{ fontFamily: T.mono, fontSize: "0.72rem", color: "#94A3B8", mt: 0.3 }}>
                    {editingLab.lab_id}
                  </Typography>
                </Box>
                <LabEditor lab={editingLab} isNew={false} onSave={saveLab}
                  onCancel={() => setEditingLab(null)} />
              </motion.div>
            )}

            {!addingLab && selectedLab && !editingLab && detailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
              <CircularProgress size={28} sx={{ color: T.navy }} />
            </Box>
          )}

          {!addingLab && selectedLab && !editingLab && selectedLabData && !detailLoading && (
              <motion.div key={selectedLab}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                {/* Lab detail view */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontFamily: T.display, fontWeight: 800, fontSize: "1.4rem", color: T.navy, letterSpacing: "-0.02em" }}>
                      {selectedLabData.display_name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Typography sx={{ fontFamily: T.mono, fontSize: "0.72rem", color: "#94A3B8" }}>
                        {selectedLabData.lab_id}
                      </Typography>
                      {selectedLabData.clia && (
                        <>
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: "#CBD5E1" }} />
                          <Typography sx={{ fontFamily: T.mono, fontSize: "0.72rem", color: "#94A3B8" }}>
                            CLIA {selectedLabData.clia}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" startIcon={<Edit sx={{ fontSize: "0.9rem !important" }} />}
                      onClick={() => setEditingLab(selectedLabData)}
                      sx={{ fontFamily: T.font, fontSize: "0.78rem", color: T.navy,
                        border: "1px solid #D1DCE8", borderRadius: "7px",
                        "&:hover": { borderColor: T.gold, color: T.goldLight, background: "rgba(192,168,140,0.07)" } }}>
                      Edit Lab
                    </Button>
                    <Button size="small" startIcon={<Delete sx={{ fontSize: "0.9rem !important" }} />}
                      onClick={() => setConfirmDeleteLab(selectedLab)}
                      sx={{ fontFamily: T.font, fontSize: "0.78rem", color: T.red,
                        border: "1px solid #FECDD3", borderRadius: "7px",
                        "&:hover": { background: "rgba(255,91,106,0.06)", borderColor: T.red } }}>
                      Delete
                    </Button>
                  </Box>
                </Box>

                {/* Info cards */}
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                  {[
                    { label: "Address", value: selectedLabData.address || "—" },
                    { label: "Medical Director", value: selectedLabData.medical_director || "—" },
                    { label: "Logo", value: selectedLabData.logo_key || "—" },
                  ].map((item) => (
                    <Box key={item.label} sx={{
                      flex: 1, minWidth: 160,
                      background: "#fff", border: "1px solid #E2EAF4",
                      borderRadius: "10px", p: 1.5,
                    }}>
                      <Typography sx={{ fontFamily: T.mono, fontSize: "0.65rem", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontFamily: T.font, fontSize: "0.83rem", color: T.navy, fontWeight: 500 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Report types */}
                <Box>
                  <Typography sx={{ fontFamily: T.mono, fontSize: "0.7rem", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>
                    Report Types · {rtValues.length}
                  </Typography>
                  {rtValues.length === 0 ? (
                    <Box sx={{
                      border: "1px dashed #D1DCE8", borderRadius: "10px",
                      p: 4, textAlign: "center",
                      color: "#94A3B8", fontFamily: T.font, fontSize: "0.83rem",
                    }}>
                      <Biotech sx={{ fontSize: 32, opacity: 0.3, mb: 1, display: "block", mx: "auto" }} />
                      No report types configured. Click <strong>Edit Lab</strong> to add one.
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {rtValues.map((rt) => (
                        <Chip
                          key={rt.id || rt.name}
                          label={rt.name || rt.id}
                          icon={<Biotech sx={{ fontSize: "0.9rem !important", color: `${T.gold} !important` }} />}
                          size="small"
                          sx={{
                            fontFamily: T.font, fontSize: "0.78rem",
                            background: "rgba(192,168,140,0.12)",
                            color: T.goldLight, border: `1px solid rgba(0,201,167,0.25)`,
                            "& .MuiChip-icon": { color: T.gold },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </motion.div>
            )}

            {!addingLab && !selectedLab && !editingLab && (
              <motion.div key="empty"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Box sx={{
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  height: "60vh", color: "#CBD5E1",
                }}>
                  <Box sx={{
                    width: 80, height: 80, borderRadius: "20px",
                    background: "linear-gradient(135deg, #F1F5F9, #E2EAF4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    mb: 2.5,
                  }}>
                    <Science sx={{ fontSize: 36, color: "#94A3B8" }} />
                  </Box>
                  <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: "1.1rem", color: "#64748B", mb: 0.5 }}>
                    Select a lab to configure
                  </Typography>
                  <Typography sx={{ fontFamily: T.font, fontSize: "0.85rem", color: "#94A3B8" }}>
                    or click <strong style={{ color: T.gold }}>New Lab</strong> to add one
                  </Typography>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

      </DialogContent>

      <ConfirmDialog
        open={!!confirmDeleteLab}
        title="Delete lab?"
        message={confirmDeleteLab ? `Permanently delete lab "${confirmDeleteLab}" and all its report types? This can't be undone.` : ""}
        onConfirm={() => deleteLab(confirmDeleteLab)}
        onCancel={() => setConfirmDeleteLab(null)}
      />
    </Dialog>
  );
}
